import { FormEvent, useEffect, useState } from "react";
import { formatDateTime } from "../lib/format";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { authApi, type SessionView } from "../api/auth";
import { adminApi } from "../api/admin";
import type { ApiError } from "../api/client";
import { totpApi, type TotpSetup } from "../api/totp";
import { useAuth } from "../auth/AuthContext";
import { useConfirm } from "../components/ConfirmDialog";
import { AvatarUpload } from "../components/AvatarUpload";
import { AppearanceCard } from "./profile/AppearanceCard";
import { NotificationChannelsCard } from "./profile/NotificationChannelsCard";

export default function ProfilePage() {
  const { state, refresh } = useAuth();
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const isWelcome = params.get("welcome") === "1";

  if (state.status !== "authenticated") return null;
  const { user } = state;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 800 }}>
      <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-ink)" }}>
        {user.firstName} {user.lastName}
      </h1>

      {isWelcome && (
        <div
          style={{
            padding: 16,
            background: "var(--brand-primary-50)",
            border: "1px solid var(--info)",
            borderRadius: 8,
            color: "var(--brand-ink)",
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          <strong>{t("profile.welcome.title", "Welcome aboard!")}</strong>
          <div style={{ marginTop: 4, color: "var(--ink)" }}>
            {t(
              "profile.welcome.body",
              "Finish setting up your profile: upload a photo and double-check your details. You can update them any time.",
            )}
          </div>
        </div>
      )}

      <Card title={t("profile.photo.title", "Profile photo")}>
        <AvatarUpload size={96} />
      </Card>

      <Card title={t("profile.account.title", "Account")}>
        <DetailRow label={t("profile.account.email", "Email")} value={user.email} />
        <DetailRow label={t("profile.account.company", "Company")} value={user.companyName ?? "—"} />
        <DetailRow label={t("profile.account.roles", "Roles")} value={user.roles.join(", ")} />
        <DetailRow label={t("profile.account.locale", "Locale")} value={user.locale} />
      </Card>

      <AppearanceCard />
      <NotificationChannelsCard />

      <PhoneCard onSuccess={refresh} />
      <ChangePasswordCard onSuccess={refresh} />
      <TwoFactorCard />
      <SessionsCard />
      <span style={{ color: "var(--ink-4)", fontSize: 12 }}>{t("footer.copyright", { year: new Date().getFullYear() })}</span>
    </div>
  );
}

function ChangePasswordCard({ onSuccess }: { onSuccess: () => Promise<void> }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    if (next !== confirm) {
      setMsg({ type: "err", text: t("profile.password.mismatch", "Passwords do not match.") });
      return;
    }
    setSubmitting(true);
    try {
      await authApi.changePassword(current, next);
      setCurrent("");
      setNext("");
      setConfirm("");
      setMsg({ type: "ok", text: t("profile.password.updated", "Password updated. Other sessions have been signed out.") });
      await onSuccess();
    } catch (err) {
      const apiErr = err as ApiError;
      setMsg({
        type: "err",
        text:
          apiErr.code === "CURRENT_PASSWORD_WRONG"
            ? t("profile.password.currentWrong", "Current password is incorrect.")
            : apiErr.message || t("profile.password.error", "Could not change password."),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card title={t("profile.password.title", "Change password")}>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Input label={t("profile.password.current", "Current password")} type="password" value={current} onChange={setCurrent} required />
        <Input label={t("profile.password.new", "New password")} type="password" value={next} onChange={setNext} required />
        <Input label={t("profile.password.confirm", "Confirm new password")} type="password" value={confirm} onChange={setConfirm} required />
        {msg && <Banner type={msg.type}>{msg.text}</Banner>}
        <button type="submit" disabled={submitting} style={primary}>
          {submitting ? t("common.saving", "Saving…") : t("profile.password.submit", "Update password")}
        </button>
      </form>
    </Card>
  );
}

function SessionsCard() {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<SessionView[] | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      setSessions(await authApi.listSessions());
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function revoke(id: string) {
    await authApi.revokeSession(id);
    await load();
  }

  return (
    <Card title={t("profile.sessions.title", "Active sessions")}>
      {loading ? (
        <p style={{ color: "var(--ink-3)" }}>{t("common.loading", "Loading…")}</p>
      ) : !sessions || sessions.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("profile.sessions.empty", "No active sessions.")}</p>
      ) : (
        <table style={table}>
          <thead>
            <tr>
              <th style={th}>{t("profile.sessions.device", "Device")}</th>
              <th style={th}>{t("profile.sessions.ip", "IP")}</th>
              <th style={th}>{t("profile.sessions.issued", "Issued")}</th>
              <th style={th}>{t("profile.sessions.lastUsed", "Last used")}</th>
              <th style={{ ...th, textAlign: "right" }}>{t("common.actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((s) => (
              <tr key={s.id}>
                <td style={td}>{abbreviateUserAgent(s.userAgent)}</td>
                <td style={td}>{s.ipAddress ?? "—"}</td>
                <td style={td}>{formatDateTime(s.issuedAt)}</td>
                <td style={td}>{s.lastUsedAt ? formatDateTime(s.lastUsedAt) : "—"}</td>
                <td style={td}>
                  <button type="button" onClick={() => revoke(s.id)} style={dangerLink}>
                    {t("profile.sessions.revoke", "Revoke")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function abbreviateUserAgent(ua: string | null) {
  if (!ua) return "Unknown";
  const m = ua.match(/(Chrome|Firefox|Safari|Edge)\/[\d.]+/);
  const platform = ua.match(/\((Mac OS X|Windows|Linux|iPhone|Android)[^)]*\)/);
  return [m?.[0], platform?.[0]].filter(Boolean).join(" ") || ua.slice(0, 60);
}

function PhoneCard({ onSuccess }: { onSuccess: () => Promise<void> }) {
  const { state } = useAuth();
  const { t } = useTranslation();
  const initial = state.status === "authenticated" ? state.user.phone ?? "" : "";
  const userId = state.status === "authenticated" ? state.user.id : null;
  const [phone, setPhone] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  useEffect(() => setPhone(initial), [initial]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!userId) return;
    setMsg(null);
    setBusy(true);
    try {
      await adminApi.updateUser(userId, { phone } as never);
      await onSuccess();
      setMsg({ type: "ok", text: t("profile.phone.saved", "Phone number updated.") });
    } catch (e) {
      const apiErr = e as ApiError;
      setMsg({ type: "err", text: apiErr?.message ?? String(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t("profile.phone.title", "Phone")}>
      <form onSubmit={handleSubmit} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+381 11 123 4567"
          style={{ flex: 1, minWidth: 240, padding: "6px 10px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 14 }}
        />
        <button type="submit" className="btn" disabled={busy}>
          {busy ? t("common.saving", "Saving…") : t("common.save", "Save")}
        </button>
        {msg && (
          <span style={{ fontSize: 12, color: msg.type === "ok" ? "var(--ok)" : "var(--err)" }}>
            {msg.text}
          </span>
        )}
      </form>
    </Card>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={card}>
      <h2 style={{ margin: 0, fontSize: 16, color: "var(--brand-ink)" }}>{title}</h2>
      {children}
    </section>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="form-grid-2" style={{ display: "grid", gridTemplateColumns: "140px 1fr", padding: "6px 0", fontSize: 14 }}>
      <span style={{ color: "var(--ink-3)" }}>{label}</span>
      <span style={{ color: "var(--ink)" }}>{value}</span>
    </div>
  );
}

function Input({
  label,
  type = "text",
  value,
  onChange,
  required,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
      <span style={{ color: "var(--ink-3)" }}>{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        style={input}
      />
    </label>
  );
}

function Banner({ type, children }: { type: "ok" | "err"; children: React.ReactNode }) {
  const bg = type === "ok" ? "var(--ok-bg)" : "var(--err-bg)";
  const fg = type === "ok" ? "var(--ok)" : "var(--err)";
  return <div style={{ padding: 10, background: bg, color: fg, borderRadius: 6, fontSize: 13 }}>{children}</div>;
}

const card: React.CSSProperties = {
  background: "var(--surface)",
  padding: 20,
  borderRadius: 10,
  boxShadow: "var(--shadow-1)",
  display: "flex",
  flexDirection: "column",
  gap: 14,
};
const input: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 6,
  border: "1px solid var(--border-strong)",
  fontSize: 14,
};
const primary: React.CSSProperties = {
  padding: "8px 14px",
  background: "var(--brand-primary-600)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  alignSelf: "flex-start",
};
const table: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th: React.CSSProperties = { textAlign: "left", padding: "6px 8px", color: "var(--ink-3)", fontWeight: 600, borderBottom: "1px solid var(--border)" };
const td: React.CSSProperties = { padding: "8px 8px", borderBottom: "1px solid var(--border)", color: "var(--ink)" };
const dangerLink: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "var(--err)",
  fontSize: 13,
  cursor: "pointer",
  padding: 0,
};

function TwoFactorCard() {
  const { t } = useTranslation();
  const confirmDialog = useConfirm();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  useEffect(() => {
    void totpApi
      .status()
      .then((s) => setEnabled(s.enabled))
      .catch(() => setEnabled(false));
  }, []);

  async function startSetup() {
    setBusy(true);
    setMsg(null);
    try {
      setSetup(await totpApi.setup());
    } catch (err) {
      setMsg({ type: "err", text: (err as ApiError).message });
    } finally {
      setBusy(false);
    }
  }

  async function confirm(e: FormEvent) {
    e.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await totpApi.confirm(code);
      setEnabled(true);
      setSetup(null);
      setCode("");
      setRecoveryCodes(res.recoveryCodes);
      setMsg({ type: "ok", text: t("profile.twoFactor.enabledMsg", "Two-factor authentication enabled. Save the recovery codes below — they will not be shown again.") });
    } catch (err) {
      setMsg({ type: "err", text: (err as ApiError).message });
    } finally {
      setBusy(false);
    }
  }

  async function regenerate() {
    if (!(await confirmDialog({ message: t("profile.twoFactor.regenerateConfirm", "Regenerate recovery codes? Existing codes will stop working."), danger: true }))) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await totpApi.regenerateRecoveryCodes();
      setRecoveryCodes(res.recoveryCodes);
      setMsg({ type: "ok", text: t("profile.twoFactor.regeneratedMsg", "New recovery codes generated. Save them now.") });
    } catch (err) {
      setMsg({ type: "err", text: (err as ApiError).message });
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    if (!(await confirmDialog({ message: t("profile.twoFactor.disableConfirm", "Disable two-factor authentication? Your account will be less protected."), danger: true }))) return;
    setBusy(true);
    try {
      await totpApi.disable();
      setEnabled(false);
      setRecoveryCodes(null);
      setMsg({ type: "ok", text: t("profile.twoFactor.disabledMsg", "Two-factor authentication disabled.") });
    } catch (err) {
      setMsg({ type: "err", text: (err as ApiError).message });
    } finally {
      setBusy(false);
    }
  }

  if (enabled === null) return null;

  return (
    <Card title={t("profile.twoFactor.title", "Two-factor authentication")}>
      {enabled && !setup && (
        <>
          <p style={{ margin: 0, fontSize: 14, color: "var(--ink)" }}>
            <strong style={{ color: "var(--ok)" }}>{t("profile.twoFactor.enabledLabel", "Enabled.")}</strong> {t("profile.twoFactor.enabledHint", "You'll be asked for a code from your authenticator app each time you sign in.")}
          </p>
          <div style={{ display: "flex", gap: 12 }}>
            <button type="button" onClick={regenerate} style={{ ...primary, alignSelf: "flex-start" }} disabled={busy}>
              {t("profile.twoFactor.regenerate", "Regenerate recovery codes")}
            </button>
            <button type="button" onClick={disable} style={{ ...dangerLink, alignSelf: "flex-start" }} disabled={busy}>
              {t("profile.twoFactor.disable", "Disable")}
            </button>
          </div>
        </>
      )}

      {recoveryCodes && (
        <div style={{ background: "var(--warn-bg)", border: "1px solid var(--warn)", borderRadius: 8, padding: 12 }}>
          <strong style={{ color: "var(--warn)", fontSize: 13 }}>{t("profile.twoFactor.recoveryTitle", "Recovery codes")}</strong>
          <p style={{ margin: "4px 0 8px", fontSize: 12, color: "var(--warn)" }}>
            {t("profile.twoFactor.recoveryHint", "Save these somewhere safe. Each can be used once if you lose access to your authenticator.")}
          </p>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
            {recoveryCodes.map((c) => (
              <li key={c} style={{ fontFamily: "ui-monospace, monospace", fontSize: 13, color: "var(--ink)" }}>{c}</li>
            ))}
          </ul>
        </div>
      )}

      {!enabled && !setup && (
        <>
          <p style={{ margin: 0, fontSize: 14, color: "var(--ink)" }}>
            {t("profile.twoFactor.setupIntro", "Add a second step to sign-in. You'll need an authenticator app like Google Authenticator, 1Password or Authy.")}
          </p>
          <button type="button" onClick={startSetup} style={primary} disabled={busy}>
            {busy ? "…" : t("profile.twoFactor.setUp", "Set up")}
          </button>
        </>
      )}

      {setup && (
        <form onSubmit={confirm} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <p style={{ margin: 0, fontSize: 14 }}>
            {t("profile.twoFactor.scanHint", "Scan this QR (or paste the secret) into your authenticator app, then enter the 6-digit code it generates.")}
          </p>
          <code style={{ background: "var(--surface-2)", padding: 8, borderRadius: 4, fontSize: 12, wordBreak: "break-all" }}>
            {setup.provisioningUri}
          </code>
          <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
            {t("profile.twoFactor.enterManually", "Or enter manually:")} <strong>{setup.secretBase32}</strong>
          </p>
          <input
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="123456"
            style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border-strong)", fontSize: 16, letterSpacing: 4, textAlign: "center", maxWidth: 200 }}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
          <button type="submit" style={primary} disabled={busy || code.length !== 6}>
            {busy ? "…" : t("profile.twoFactor.confirm", "Confirm")}
          </button>
        </form>
      )}

      {msg && (
        <div style={{ padding: 8, fontSize: 13, borderRadius: 6, background: msg.type === "ok" ? "var(--ok-bg)" : "var(--err-bg)", color: msg.type === "ok" ? "var(--ok)" : "var(--err)" }}>
          {msg.text}
        </div>
      )}
    </Card>
  );
}
